import { action, computed, makeObservable, observable, ObservableMap, values } from 'mobx';

import { IDiscoverDestination } from 'APP/model/tenantRouter/tenantRouterModel.types';
import { ProjectInstallationType } from 'CONFIG_TYPES';

import { cachingConnectionToken } from './Services/cachingConnectionToken';

export class AuthStore {
  @observable isUntrusted = false;
  @observable isDeleted = false;
  @observable connectionToken: string | null = null;
  @observable isFirstAuthDone = false; // check if auth was processed 1\st time
  @observable session: string | null = null;
  @observable streamToken: string | null = null; // token3 from api
  @observable isGuestCallSession = false;
  @observable isNeedForceUpdate = false;
  @observable isNeedSoftUpdate = false;
  @observable isNeedDesktopSoftUpdate = false;
  @observable discoverDestinationsMap: ObservableMap<string, IDiscoverDestination> =
    new ObservableMap();
  preferredDiscoverDestinationKey = '';

  constructor() {
    makeObservable(this);

    cachingConnectionToken(this);
  }

  @computed
  get discoverDestinations(): IDiscoverDestination[] {
    return [...values(this.discoverDestinationsMap)].sort((a, b) => {
      const aPreferred = a.key === this.preferredDiscoverDestinationKey ? 0 : 1;
      const bPreferred = b.key === this.preferredDiscoverDestinationKey ? 0 : 1;

      if (aPreferred !== bPreferred) {
        return aPreferred - bPreferred;
      }

      const aLocal = a.isLocal ? 0 : 1;
      const bLocal = b.isLocal ? 0 : 1;

      return aLocal - bLocal;
    });
  }

  @computed
  get regionsDestinations(): IDiscoverDestination[] {
    return this.discoverDestinations.filter(
      (item) => item.type === ProjectInstallationType.CloudOnly
    );
  }

  @computed
  get serversDestinations(): IDiscoverDestination[] {
    return this.discoverDestinations.filter(
      (item) => item.type === ProjectInstallationType.OnPremiseOnly
    );
  }

  @computed
  get localDestinations(): IDiscoverDestination[] {
    return this.discoverDestinations.filter((item) => item.isLocal);
  }

  @action
  setDeleted(value: boolean): void {
    this.isDeleted = value;
  }

  @action
  setIsUntrusted(value: boolean): void {
    this.isUntrusted = value;
  }

  @computed
  get isAuthenticated(): boolean {
    return Boolean(this.session && this.streamToken);
  }

  @action
  setConnectionToken(token: string): void {
    this.connectionToken = token;
  }

  @action
  setForceUpdate(value: boolean): void {
    this.isNeedForceUpdate = value;
  }

  @action
  setSoftUpdate(value: boolean): void {
    this.isNeedSoftUpdate = value;
  }

  @action
  setDesktopSoftUpdate(value: boolean): void {
    this.isNeedDesktopSoftUpdate = value;
  }

  @action
  setSession(sessionId: string): void {
    this.session = sessionId;
  }

  @action
  setStreamToken(streamToken: string): void {
    this.streamToken = streamToken;
  }

  @action
  setIsGuestCallSession(value: boolean): void {
    this.isGuestCallSession = value;
  }

  @action
  finishFirstLogin(): void {
    this.isFirstAuthDone = true;
  }

  @action
  setDiscoverDestinations(destinations: IDiscoverDestination[]): void {
    this.discoverDestinationsMap.clear();
    this.addDiscoverDestinations(destinations);
  }

  @action
  addDiscoverDestinations(destinations: IDiscoverDestination[]): void {
    for (const destination of destinations) {
      this.discoverDestinationsMap.set(destination.key, destination);
    }
  }

  @action
  removeDiscoverDestination(key: string): void {
    this.discoverDestinationsMap.delete(key);
  }

  getDiscoverDestinationByKey(key: string): IDiscoverDestination | null {
    return this.discoverDestinationsMap.get(key) || null;
  }

  @action
  reset(): void {
    this.discoverDestinationsMap.clear();
    this.setDeleted(false);
    this.session = null;
    this.connectionToken = null;
    this.streamToken = null;
    this.isGuestCallSession = false;
  }
}
